import React, { useState, useEffect } from "react";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import sendIcon from "src/images/send_icon.png";
import BotMessage from "src/components/shared/botMessage/botMessage";
import ChatResult from "src/components/pages/home/components/chat/chatResult/chatResult";
import { getWebSocketUrl } from "src/config/websocketConfig";
import { formatISO } from "date-fns";
import "./gap.scss";

let ws: WebSocket | null = null;

interface GapResponse {
  type: "status" | "result";
  content: string | any;
  id: string;
  timestamp: string;
}

const GapPage: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [responses, setResponses] = useState<GapResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dots, setDots] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleNewAnalysis = () => {
    setSelectedFile(null);
    setResponses([]);
    setIsLoading(false);
    const fileInput = document.getElementById("file-input") as HTMLInputElement;
    if (fileInput) fileInput.value = "";
  };

  const handleAnalyze = async () => {
    if (!selectedFile) return;

    setIsLoading(true);

    try {
      const fileContent = await convertFileToBase64(selectedFile);

      const wsUrl = getWebSocketUrl();

      const { getAuthHeaders } = await import("src/utils/authUtils");
      const headers = await getAuthHeaders();
      const authToken = headers.authorization?.replace("Bearer ", "") || "";
      const federateToken =
        headers["federate-authorization"]?.replace("Bearer ", "") || "";

      const params = new URLSearchParams();
      if (authToken) params.append("Authorization", authToken);
      if (federateToken) params.append("federate-authorization", federateToken);

      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      params.append("sessionId", sessionId);

      const wsUrlWithAuth = `${wsUrl}/ws/gap?${params.toString()}`;

      if (!ws || ws.readyState !== WebSocket.OPEN) {
        ws = new WebSocket(wsUrlWithAuth);

        ws.onopen = () => {
          ws!.send(
            JSON.stringify({
              file_name: selectedFile.name,
              file_content: fileContent,
            }),
          );
        };

        ws.onmessage = (event) => {
          try {
            const responseId = `gap_${Date.now()}_${Math.random()}`;
            const response = JSON.parse(event.data);

            if (response.result) {
              const resultContent = {
                messageId: responseId,
                datetime: formatISO(new Date()),
                isTextOnly: true,
                llmMessage: response.result.system_message || "",
                sourceAttributions:
                  response.result.enhanced_source_attributions || [],
                relatedDocuments:
                  response.result.enhanced_related_documents || [],
              };
              setResponses((prev) => [
                ...prev,
                {
                  type: "result",
                  content: resultContent,
                  id: responseId,
                  timestamp: formatISO(new Date()),
                },
              ]);
            } else if (response.status) {
              setResponses((prev) => [
                ...prev,
                {
                  type: "status",
                  content: response.status,
                  id: responseId,
                  timestamp: formatISO(new Date()),
                },
              ]);
            }
          } catch (error) {
            console.error("Error parsing response:", error);
            setIsLoading(false);
          }
        };

        ws.onerror = (error) => {
          console.error("WebSocket error:", error);
          setIsLoading(false);
        };

        ws.onclose = () => {
          setIsLoading(false);
          ws = null;
        };
      } else {
        ws.send(
          JSON.stringify({
            file_name: selectedFile.name,
            file_content: fileContent,
          }),
        );
      }
    } catch (error) {
      console.error("Error analyzing file:", error);
      setIsLoading(false);
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  return (
    <div className="gap-page">
      <div className="gap-content">
        <Text type="h600" alignment="center">
          WHS Gap Analyzer
        </Text>

        <Text type="b400" alignment="center" className="instructions">
          This tool helps find gaps between laws or regulations and internal WHS
          procedures.<p></p>
          Choose a law document on your local computer, and click the arrow to
          start the analysis.
        </Text>

        <div className="file-upload-section">
          <input
            type="file"
            id="file-input"
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.txt"
            className="file-input"
          />
          <label htmlFor="file-input" className="file-label">
            {selectedFile ? selectedFile.name : "Choose Document"}
          </label>
          <Button
            type="icon"
            onClick={handleAnalyze}
            disabled={!selectedFile || isLoading}
            className="analyze-button"
          >
            <img
              src={sendIcon}
              alt="Analyze Document"
              className="analyze-icon"
            />
          </Button>
        </div>

        {responses.length > 0 && (
          <div className="responses-section">
            {responses.map((response, index) => (
              <div key={response.id} className="gap-response">
                {response.type === "result" ? (
                  <ChatResult output={response.content} isOnMcpRoute={false} />
                ) : (
                  <BotMessage
                    className="gap-status-message"
                    botName="Saige"
                    datetime={response.timestamp}
                  >
                    <div className="status-message-content">
                      {response.content}
                      {index === responses.length - 1 &&
                      response.type === "status" ? (
                        <span className="thinking-dots">{dots}</span>
                      ) : (
                        <span className="status-checkmark">✓</span>
                      )}
                    </div>
                  </BotMessage>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Button
        type="primary"
        onClick={handleNewAnalysis}
        className="floating-new-analysis-btn"
      >
        New Analysis
      </Button>
    </div>
  );
};

export default GapPage;
